<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  toRefs,
  watch,
  type StyleValue,
} from "vue";
import {
  type DisplayStrategy,
  type DropdownDrawerTypes,
  type PlacementTypes,
  type WidthStrategy,
} from "@/types";
import { nanoid } from "nanoid";
import { onClickOutside, useBreakpoints } from "@vueuse/core";
import { BREAKPOINTS } from "@/consts";
import DropdownMenuContainer, {
  type ContentOverflow,
} from "@/stories/buttons/DropdownButton/DropdownMenuContainer.vue";
import { useElementRect } from "@/composables/useElementRect";

const triggerBoxId = `trigger-box-${nanoid()}`;

interface Props {
  label?: string;
  placement?: PlacementTypes;
  fullWidth?: boolean;
  isActive?: boolean;
  drawer?: DropdownDrawerTypes;
  saveButtonKey?: string;
  saveButtonDisplayment?: DisplayStrategy;
  widthStrategy?: WidthStrategy;
  ignoreCustomStylesOnMobile?: boolean;
  contentOverflow?: ContentOverflow;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  placement: "left",
  fullWidth: false,
  isActive: false,
  drawer: "auto",
  saveButtonKey: "global.save",
  saveButtonDisplayment: "auto",
  widthStrategy: "fit",
  ignoreCustomStylesOnMobile: true,
  contentOverflow: "overflow-auto",
});

const emit = defineEmits<{
  click: [];
  save: [];
  "click-outside": [];
  close: [];
}>();

const ACCEPTABLE_BOTTOM_SPACE = 300;
const MAX_CONTENT_HEIGHT = 600;

const {
  placement,
  drawer,
  widthStrategy,
  ignoreCustomStylesOnMobile,
  disabled,
} = toRefs(props);
const menuRef = ref();
const trigger = ref<Element | null>(null);
const triggerRect = useElementRect(trigger);
const menuContentHeight = ref();
const triggerBoxRef = ref<HTMLElement>();

type ListDirection = "up" | "down";

const listDirection = ref<ListDirection>("down");

const breakpoints = useBreakpoints(BREAKPOINTS);
const isMobile = breakpoints.smaller("tablet");
const show = ref(props.isActive);

const possibleBottomSpace = computed(() => {
  const triggerTop = triggerRect.value?.top || 0;
  const triggerHeight = triggerRect.value?.height || 0;
  return window.innerHeight - triggerTop - triggerHeight;
});

const setContentDirection = () => {
  // If there is not any space what can be used, the content should be positioned
  // at the above of the trigger.
  if (possibleBottomSpace.value < ACCEPTABLE_BOTTOM_SPACE) {
    listDirection.value = "up";
  } else {
    listDirection.value = "down";
  }
};

const handleClick = () => {
  show.value = !show.value;
  emit("click");
  setContentDirection();
};

onMounted(async () => {
  await nextTick();

  if (
    triggerBoxRef.value &&
    triggerBoxRef.value.firstElementChild &&
    !disabled.value
  ) {
    trigger.value = triggerBoxRef.value.firstElementChild;
    triggerBoxRef.value.firstElementChild.addEventListener(
      "click",
      handleClick,
    );
  }
});

onBeforeUnmount(() => {
  if (
    triggerBoxRef.value &&
    triggerBoxRef.value.firstElementChild &&
    !disabled.value
  ) {
    triggerBoxRef.value.firstElementChild.removeEventListener(
      "click",
      handleClick,
    );
  }
});

const handleContentClick = (event: MouseEvent) => {
  const clickedElement = event.target as HTMLElement;
  if (clickedElement.closest(".dropdown-trigger-no-close")) {
    return;
  }

  close();
};

const handleSave = () => {
  close();
  emit("save");
};

onClickOutside(menuRef, (element) => {
  const clickedElement = element.target as HTMLElement;

  if (clickedElement.closest(`#${triggerBoxId}`) === null) {
    close();
  }

  emit("click-outside");
});

const customStyle = computed(() => {
  const styles: Partial<StyleValue> = {};

  if (!window) {
    return styles;
  }

  if (ignoreCustomStylesOnMobile.value && isMobile.value) {
    return styles;
  }

  // For unit tests.
  if (!window) {
    return styles;
  }

  // Check the placement of the content
  if (placement.value === "right") {
    const value = window.innerWidth - Math.round(triggerRect.value?.right || 0);
    styles.right = `${value}px`;
  }

  // Set the width value
  const width = triggerRect.value?.width
    ? Math.round(triggerRect.value.width)
    : undefined;

  if (width && widthStrategy.value === "full") {
    styles.width = `${width}px`;
    styles.minWidth = "250px";
  }

  // What is the trigger's height? This value is important to positioning the content
  const triggerHeight = (triggerRect.value?.height || 0) + 4;
  const triggerTop = triggerRect.value?.top || 0;

  // If there is not any space what can be used, the content should be positioned
  // at the above of the trigger.
  if (listDirection.value === "down") {
    const top = triggerTop + triggerHeight;
    const maxHeight = Math.min(
      possibleBottomSpace.value - 20,
      MAX_CONTENT_HEIGHT,
    );

    styles.top = `${top}px`;
    styles.maxHeight = `${maxHeight}px`;
  } else {
    const top = triggerTop - menuContentHeight.value - 20;
    styles.top = `${top}px`;
    styles.maxHeight = `${MAX_CONTENT_HEIGHT}px`;
  }

  styles.position = "fixed";

  return styles;
});

const close = () => {
  show.value = false;
  emit("close");
};

watch(
  () => props.isActive,
  () => {
    show.value = props.isActive;
    if (props.isActive === false) {
      close();
    }
  },
);
</script>

<template>
  <div class="relative" :class="{ 'w-full': fullWidth, 'w-fit': !fullWidth }">
    <span
      :id="triggerBoxId"
      ref="triggerBoxRef"
      class="text-nowrap dropdown-trigger-box"
      :class="[
        fullWidth ? 'w-full block' : 'w-fit inline-block',
        { 'is-trigger-active': isActive },
      ]"
    >
      <slot name="trigger" :is-active="show"></slot>
    </span>

    <DropdownMenuContainer
      v-if="show"
      ref="menuRef"
      :widthStrategy="widthStrategy"
      :drawer="drawer"
      :title="label"
      :customStyle="customStyle"
      :placement="placement"
      :fullWidth="fullWidth"
      :saveButtonKey="saveButtonKey"
      :saveButtonDisplayment="saveButtonDisplayment"
      :contentOverflow="contentOverflow"
      @click="handleContentClick"
      @save="handleSave"
      @cancel="close"
      @onContentHeight="(value) => (menuContentHeight = value)"
    >
      <slot name="content"></slot>
    </DropdownMenuContainer>
  </div>
</template>
